import { useRef } from 'react'
import { Canvas, useLoader, useFrame } from '@react-three/fiber'
//import './styles.css'
import { Mesh, TextureLoader } from 'three'
//import { TrackballControls } from 'three-trackballcontrols-ts';
import { TrackballControls } from '@react-three/drei'


import './LegoEarth.css';

interface PlanetProps
{
    radius: number, 
    texture: string,
    index: number
}

function Planet(props:PlanetProps) {
    const meshRef = useRef<Mesh>(null!)
    const colorMap = useLoader(TextureLoader, props.texture)

    let dateTime = new Date().getTime();

    useFrame(() => {
        meshRef.current.rotation.y += 0.001;
        if(props.index >= 0)
        {
            dateTime += 10000000;
            let coords = computeEclipticCoordinates(keplerianElements2050, props.index, dateTime);
            meshRef.current.position.x = coords[1] * 10;
            meshRef.current.position.y = coords[2] * 10;
            meshRef.current.position.z = coords[0] * 10;
        }
    }
    )

    const rotations:number[] = [0,0,0,0,23,0,0,0,0,0];

    return (
        <mesh ref={meshRef} rotation={[rotations[props.index+1]*3.14/180,0,0]} >
            <sphereGeometry attach="geometry" args={[props.radius, 64, 64]} />
            <meshPhongMaterial attach="material" map={colorMap} specular={1}/>
        </mesh>
    )
}
//import './LegoEarth.css';


function computeEclipticCoordinates(keplerianElementsTable:number[][], planetIndex:number, UNIXMilliTime:number){
	let deg2rad = Math.PI/180;
	let Teph = UNIXMilliTime/1000/86400 + 2440587.5; //Julian Ephemeris Date
	let T = (Teph-2451545)/36525; //Centuries past J2000.0
	
	//Calculations of the Keplerian elements
	let a0 = keplerianElementsTable[planetIndex][1]; //semi-major axis (initial)
	let at = keplerianElementsTable[planetIndex][7]; //semi-major axis (century variation)
	let a = a0+at*T; //semi-major axis
	let e0 = keplerianElementsTable[planetIndex][2]; //eccentricity (initial)
	let et = keplerianElementsTable[planetIndex][8]; //eccentricity (century variation)
	let e = e0+et*T; //eccentricity 
	let I0 = keplerianElementsTable[planetIndex][3]; //inclination (initial)
	let It = keplerianElementsTable[planetIndex][9]; //inclination (century variation)
	let I = I0 + It*T; //inclination
	let L0 = keplerianElementsTable[planetIndex][4]; //mean longitude (initial)
	let Lt = keplerianElementsTable[planetIndex][10]; //mean longitude (century variation)
	let L = L0+Lt*T; //mean longitude
	let W0 = keplerianElementsTable[planetIndex][5]; //longitude of perihelion (initial)
	let Wt = keplerianElementsTable[planetIndex][11]; //longitude of perihelion (century variation)
	let W = W0+Wt*T; //longitude of perihelion
	let O0 = keplerianElementsTable[planetIndex][6]; //longitude of ascending node (initial)
	let Ot = keplerianElementsTable[planetIndex][12]; //longitude of ascending node (century variation)
	let O = O0+Ot*T; //longitude of ascending node
	
	//Auxiliary parameters for the 3000 BC - 3000 AD period
	let b = keplerianElementsTable[planetIndex][13];
	let c = keplerianElementsTable[planetIndex][14];
	let s = keplerianElementsTable[planetIndex][15];
	let f = keplerianElementsTable[planetIndex][16];
	
	//Argument of perihelion w
	let w = W-O;
	
	//Mean anomaly M
	let M = L-W+b*Math.pow(T,2)+c*Math.cos(deg2rad*f*T)+s*Math.sin(deg2rad*f*T);
	if(M < -180 || M > 180) M = M % 360; //Modulus M to be between -180 and 180
	
	//Solve the Kepler Equation for the eccentric anomaly E
	let E = solveKeplerEquation(M,e); 
	
	//Heliocentric coordinates
	let x1 = a*(Math.cos(deg2rad*E)-e);
	let y1 = a*Math.sqrt(1-(e*e))*Math.sin(deg2rad*E);
	let z1 = 0;
	
	//Auxiliary trigonometric calculations
	let cosw = Math.cos(deg2rad*w);
	let sinw = Math.sin(deg2rad*w);
	let cosO = Math.cos(deg2rad*O); 
	let sinO = Math.sin(deg2rad*O);
	let cosI = Math.cos(deg2rad*I);
	let sinI = Math.sin(deg2rad*I);
	
	//J2000 Ecliptic Plane Coordinates
	let Xecl = (cosw*cosO - sinw*sinO*cosI)*x1 + (-sinw*cosO - cosw*sinO*cosI)*y1;
	let Yecl = (cosw*sinO + sinw*cosO*cosI)*x1 + (-sinw*sinO + cosw*cosO*cosI)*y1;
	let Zecl = (sinw*sinI)*x1 + (cosw*sinI)*y1;
	
	//Result array
	let EclipticCoordinates = [Xecl, Yecl, Zecl];
	
	return EclipticCoordinates;
}

function solveKeplerEquation(M:number,e:number) {
	let e1 = 180/Math.PI*e;
	let deg2rad = Math.PI/180;
	let E0 = M+e1*Math.sin(M*deg2rad);
	let tolerance = 10e-6;
	let dE;
	let dM;
	let E1 = E0;
	let E2 = 0;
	let difE1E2 = 1;
	
	for (let i=0; (i<1000) && (difE1E2 > tolerance); i++){
		dM = M - (E1 - e1*Math.sin(deg2rad*E1));
		dE = dM/(1-e*Math.cos(deg2rad*E1));
		E2=E1+dE;
		difE1E2 = Math.abs(E1-E2);
	}
	return E2;
}


//Keplerian elements and their rates, with respect to the mean ecliptic
//and equinox of J2000, valid for the time-interval 1800 AD - 2050 AD.
//
//               a0              e0               I0                L0                 W0              O0                 at               et               It                 Lt               Wt               Ot             b           c           s         f
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
let keplerianElements2050 = [
[0,   0.38709927,      0.20563593,      7.00497902,      252.25032350,     77.45779628,     48.33076593,        0.00000037 ,     0.00001906 ,     -0.00594749,    149472.67411175,      0.16047689,     -0.12534081,     0,          0,         0,         0],
[1,     0.72333566,      0.00677672,      3.39467605,      181.97909950,    131.60246718,     76.67984255,        0.00000390 ,     -0.00004107,     -0.00078890,    58517.81538729 ,      0.00268329,     -0.27769418,     0,          0,         0,         0],
[2,   1.00000261,      0.01671123,     -0.00001531,      100.46457166,    102.93768193,      0.0       ,   	    0.00000562 ,     -0.00004392,     -0.01294668,    35999.37244981 ,      0.32327364,     0.0        ,     0,          0,         0,         0],
[3,	  1.52371034,	   0.09339410,	    1.84969142, 	  -4.55343205, 	  -23.94362959, 	49.55953891,        0.00001847 , 	-0.00007882 ,	  -0.00813131,	  19140.30268499 ,	    0.44441088, 	-0.29257343, 	 0, 	     0, 	    0, 	   	   0],
[4,   5.20288700,      0.04838624,      1.30439695,       34.39644051,     14.72847983,    100.47390909,        -0.00011607,    -0.00013253 ,     -0.00183714,     3034.74612775 ,      0.21252668,     0.20469106 ,     0,          0,         0,         0],
[5,    9.53667594,      0.05386179,      2.48599187,       49.95424423,     92.59887831,    113.66242448,        -0.00125060,    -0.00050991 ,      0.00193609,     1222.49362201 ,     -0.41897216,     -0.28867794,     0,          0,         0,         0],
[6,   19.18916464,      0.04725744,      0.77263783,      313.23810451,    170.95427630,     74.01692503,        -0.00196176,    -0.00004397 ,     -0.00242939,     428.48202785  ,      0.40805281,      0.04240589,     0,          0,         0,         0],
[7,  30.06992276,      0.00859048,      1.77004347,      -55.12002969,     44.96476227,    131.78422574,         0.00026291,     0.00005105 ,      0.00035372,      218.45945325 ,     -0.32241464,     -0.00508664,     0,          0,         0,         0],
[8,    39.48211675,      0.24882730,     17.14001206,      238.92903833,    224.06891629,    110.30393684,        -0.00031596,     0.00005170 ,      0.00004818,      145.20780515 ,     -0.04062942,     -0.01183482,     0,          0,         0,         0]
];



//Keplerian elements and their rates, with respect to the mean ecliptic and equinox of J2000,
//valid for the time-interval 3000 BC -- 3000 AD.
//
//
//               a0              e0               I0                L0                 W0              O0                 at               et               It                 Lt               Wt               Ot                  b                c             s                f
//---------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
let keplerianElements3000 = [
[0,   0.38709843,      0.20563661,      7.00559432,      252.25166724,     77.45771895,     48.33961819,        0.00000000,      0.00002123,     -0.00590158,   149472.67486623,      0.15940013,     -0.12214182,     0,               0,              0,             0],
[1,     0.72332102,      0.00676399,      3.39777545,      181.97970850,    131.76755713,     76.67261496,       -0.00000026,     -0.00005107,      0.00043494,    58517.81560260,      0.05679648,     -0.27274174,     0,               0,              0,             0],
[2,   1.00000018,      0.01673163,     -0.00054346,      100.46691572,    102.93005885,     -5.11260389,       -0.00000003,     -0.00003661,     -0.01337178,    35999.37306329,      0.31795260,     -0.24123856,     0,               0,              0,             0],
[3,      1.52371243,      0.09336511,      1.85181869,       -4.56813164,    -23.91744784,     49.71320984,        0.00000097,      0.00009149,     -0.00724757,    19140.29934243,      0.45223625,     -0.26852431,     0,               0,              0,             0],
[4,   5.20248019,      0.04853590,      1.29861416,       34.33479152,     14.27495244,    100.29282654,       -0.00002864,      0.00018026,     -0.00322699,     3034.90371757,      0.18199196,      0.13024619,     -0.00012452,     0.06064060,     -0.35635438,   38.35125000],
[5,    9.54149883,      0.05550825,      2.49424102,       50.07571329,     92.86136063,    113.63998702,       -0.00003065,     -0.00032044,      0.00451969,     1222.11494724,      0.54179478,     -0.25015002,     0.00025899,      -0.13434469,    0.87320147,    38.35125000],
[6,   19.18797948,      0.04685740,      0.77298127,      314.20276625,    172.43404441,     73.96250215,       -0.00020455,     -0.00001550,     -0.00180155,      428.49512595,      0.09266985,      0.05739699,     0.00058331,      -0.97731848,    0.17689245,    7.67025000],
[7,  30.06952752,      0.00895439,      1.77005520,      304.22289287,     46.68158724,    131.78635853,        0.00006447,      0.00000818,      0.00022400,      218.46515314,      0.01009938,     -0.00606302,     -0.00041348,     0.68346318,     -0.10162547,   7.67025000],
[8,    39.48686035,      0.24885238,     17.14104260,      238.96535011,    224.09702598,    110.30167986,        0.00449751,      0.00006016,      0.00000501,      145.18042903,     -0.00968827,     -0.00809981,     -0.01262724,     0,              0,             0]
];


function LegoEarth() {
//	renderer.setSize(width, height);

  return (
    <div className='test123'>
        <Canvas>
            <ambientLight intensity={0.4} />
            <pointLight position={[0,0,0]} intensity={1000} />
            <Planet radius={2} texture='../assets/LegoPlanets/LegoGlobe/earth.png' index={-1} />
            <Planet radius={1} texture='../assets/LegoPlanets/LegoGlobe/earth.png' index={0} />
            <Planet radius={1} texture='../assets/LegoPlanets/LegoGlobe/earth.png' index={1} />
            <Planet radius={1} texture='../assets/LegoPlanets/LegoGlobe/earth.png' index={2} />
            <Planet radius={1} texture='../assets/LegoPlanets/LegoGlobe/earth.png' index={3} />
            <Planet radius={1} texture='../assets/LegoPlanets/LegoGlobe/earth.png' index={4} />
            <Planet radius={1} texture='../assets/LegoPlanets/LegoGlobe/earth.png' index={5} />
            <Planet radius={1} texture='../assets/LegoPlanets/LegoGlobe/earth.png' index={6} />
            <Planet radius={1} texture='../assets/LegoPlanets/LegoGlobe/earth.png' index={7} />
            <Planet radius={1} texture='../assets/LegoPlanets/LegoGlobe/earth.png' index={8} />
            <TrackballControls />
        </Canvas>
    </div>
  )
}



export default LegoEarth;





/*

import * as THREE from 'three';
import { TrackballControls } from 'three-trackballcontrols-ts';
import { Detector } from 'detector-js';
//import {PerspectiveCamera} from "/Users/nathanbain/Desktop/reactTest/testapp3/node_modules/@types/three/src/cameras/PerspectiveCamera"
import { useEffect } from 'react';

import useScript from '../useScript';

import './LegoEarth.css';

function LegoEarth()
{
    //useScript('../js/LegoPlanets/three.min.js');
    //useScript('../js/LegoPlanets/Detector.js');
    //useScript('../js/LegoPlanets/TrackballControls.js');
    //useScript('../js/LegoPlanets/earth.js');

    useEffect(() => {
        var element =  document.getElementById('LegoEarth');
if (typeof(element) != 'undefined' && element != null && !element.hasChildNodes())
{
        // call api or anything
        console.log("loaded");
     

    var webglEl = document.getElementById('LegoEarth');

    const detector = new Detector();

	// if (!detector.feature.webgl) {
	// 	detector.feature.addGetWebGLMessage(webglEl);
	// 	return;
	// }

	var width  = window.innerWidth,
		height = window.innerHeight;

    // Earth params
	var radius   = 0.5,
    segments = 32,
    rotation = 6;  

    var scene = new THREE.Scene();

    var camera = new THREE.PerspectiveCamera(45, width / height, 0.01, 1000);
	camera.position.z = 1.5;

	var renderer = new THREE.WebGLRenderer();
	renderer.setSize(width, height);

	scene.add(new THREE.AmbientLight(0x333333));

	var light = new THREE.DirectionalLight(0xffffff, 1);
	light.position.set(5,3,5);
	scene.add(light);

    var sphere = createSphere(radius, segments);
	sphere.rotation.y = rotation; 
	scene.add(sphere)

//    var clouds = createClouds(radius, segments);
//	clouds.rotation.y = rotation;
//	scene.add(clouds)

	var stars = createStars(90, 64);
	scene.add(stars);

	var controls = new TrackballControls(camera,renderer.domElement);

    if(webglEl != null)
    {
        console.log("adding renderer")
    	webglEl.appendChild(renderer.domElement);
    }

	render(controls, sphere, renderer, scene, camera);
}
});

    function render(controls: TrackballControls, sphere: THREE.Mesh<THREE.SphereGeometry, THREE.MeshPhongMaterial>, renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.PerspectiveCamera) {
		controls.update();
		sphere.rotation.y += 0.0005;
		//clouds.rotation.y += 0.0005;		
		requestAnimationFrame(() => {render(controls, sphere, renderer, scene, camera)});
		renderer.render(scene, camera);
	}

	function createSphere(radius: number, segments: number) {
        var tex = new THREE.Texture(); // create a material

        // load a resource
    var loader = new THREE.TextureLoader().load(
                    // resource URL
            '../assets/LegoPlanets/LegoGlobe/earth.png',
            // Function when resource is loaded
            function ( texture ) {
                // do something with the texture
                tex = texture; // set the material's map when when the texture is loaded
            });

		return new THREE.Mesh(
			new THREE.SphereGeometry(radius, segments, segments),
			new THREE.MeshPhongMaterial({
				map:         tex,
				//bumpMap:     THREE.ImageUtils.loadTexture('images/elev_bump_4k.jpg'),
				//bumpScale:   0.005,
			//	specularMap: THREE.ImageUtils.loadTexture('images/water_4k.png'),
			//	specular:    new THREE.Color('grey')	
			})
		);
	}

	function createClouds(radius: number, segments: number) {
		return new THREE.Mesh(
			new THREE.SphereGeometry(radius + 0.003, segments, segments),			
			new THREE.MeshPhongMaterial({
				//map:         THREE.ImageUtils.loadTexture('images/fair_clouds_4k.png'),
				//transparent: true
			})
		);		
	}

	function createStars(radius: number, segments: number) {
        var tex = new THREE.Texture(); // create a material

        // load a resource
    var loader = new THREE.TextureLoader().load(
                    // resource URL
            '../assets/LegoPlanets/LegoGlobe/galaxy_starfield.png',
            // Function when resource is loaded
            function ( texture ) {
                // do something with the texture
                tex = texture; // set the material's map when when the texture is loaded
            });

		return new THREE.Mesh(
			new THREE.SphereGeometry(radius, segments, segments), 
			new THREE.MeshBasicMaterial({
				map:  tex, 
				side: THREE.BackSide
			})
		);
	}

    return (
        <div>
            <div id="LegoEarth"></div>
        </div>
    )
}

export default LegoEarth;
*/